import { ReactNode, useState } from 'react';
import { Btn, Eyebrow, listReset } from '../components/primitives';
import buildPageEdit from '../assets/build-page-edit.png';
import buildQbscript from '../assets/qbscript.png';
import buildDash from '../assets/dash.png';
import storeImg from '../assets/store.png';
import metricsImg from '../assets/metrics.png';
import spellsImg from '../assets/spells.png';

interface SectionHeaderProps {
  num: string;
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  side?: boolean;
}

const SectionHeader = ({ num, eyebrow, title, lead, side }: SectionHeaderProps) => (
  <div
    className={side ? 'qb-section-header-side' : undefined}
    style={{
      display: 'grid',
      gridTemplateColumns: side ? '1.2fr 1fr' : '1fr',
      gap: 48,
      alignItems: 'end',
      marginBottom: 56,
    }}>
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 18 }}>
        <span
          className="qb-section-num"
          style={{
            fontFamily: 'var(--qb-font-display)',
            fontSize: 56,
            color: 'var(--qb-orange-500)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}>
          {num}
        </span>
        <Eyebrow>{eyebrow}</Eyebrow>
      </div>
      <h2
        className="qb-section-h2"
        style={{
          fontFamily: 'var(--qb-font-display)',
          textTransform: 'uppercase',
          fontSize: 60,
          lineHeight: 0.95,
          letterSpacing: '-0.015em',
          margin: '16px 0 0',
          textWrap: 'balance',
          maxWidth: 760,
        }}>
        {title}
      </h2>
    </div>
    {side && (
      <p
        style={{
          fontFamily: 'var(--qb-font-body)',
          fontSize: 17,
          lineHeight: 1.6,
          color: 'var(--qb-fg-muted)',
          margin: 0,
          maxWidth: 440,
          justifySelf: 'end',
        }}>
        {lead}
      </p>
    )}
  </div>
);

interface BuildItem {
  eye: string;
  t: string;
  b: string;
  img: string;
  alt: string;
}

const BUILD_ITEMS: BuildItem[] = [
  {
    eye: 'Drag-and-drop',
    t: 'Page Editor',
    b: 'Design character sheets and reference pages with a real layout canvas. Pixel-perfect, on every device.',
    img: buildPageEdit,
    alt: 'Quest Bound Page Editor — character sheet canvas with attribute hexes, saving throw boxes, hero artwork, and a Content properties panel',
  },
  {
    eye: 'QBScript',
    t: 'Automate the math',
    b: 'Write rules in QBScript. Saving throws, conditions, derived stats — all of it runs itself.',
    img: buildQbscript,
    alt: 'Quest Bound QBScript editor',
  },
  {
    eye: 'System-agnostic',
    t: 'Any RPG, any system',
    b: 'D20, PbtA, OSR, your homebrew. Quest Bound is a tabletop game engine, not a template for one ruleset.',
    img: buildDash,
    alt: 'Quest Bound dashboard for system-agnostic ruleset authoring',
  },
];

const BuildEditorMock = ({ src, alt }: { src: string; alt: string }) => (
  <div
    style={{
      background: 'var(--qb-ink-700)',
      border: '1px solid var(--qb-border-strong)',
      borderRadius: 6,
      overflow: 'hidden',
      boxShadow: 'var(--qb-shadow-lg)',
      display: 'flex',
    }}>
    <img
      src={src}
      alt={alt}
      style={{ width: '100%', height: 'auto', display: 'block' }}
    />
  </div>
);

const BuildSideCards = ({
  activeIndex,
  onSelect,
}: {
  activeIndex: number;
  onSelect: (i: number) => void;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {BUILD_ITEMS.map((it, i) => {
      const active = activeIndex === i;
      return (
        <button
          key={it.t}
          type="button"
          onClick={() => onSelect(i)}
          aria-pressed={active}
          style={{
            background: active ? 'var(--qb-ink-700)' : 'var(--qb-ink-800)',
            border: active
              ? '1px solid var(--qb-orange-500)'
              : '1px solid var(--qb-border-strong)',
            boxShadow: active ? '0 0 0 1px var(--qb-orange-500) inset' : 'none',
            borderRadius: 6,
            padding: '22px 24px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            cursor: 'pointer',
            textAlign: 'left',
            color: 'inherit',
            font: 'inherit',
            transition:
              'background 200ms var(--qb-ease-standard), border-color 200ms var(--qb-ease-standard), box-shadow 200ms var(--qb-ease-standard)',
          }}>
          <Eyebrow color={active ? 'var(--qb-orange-500)' : 'var(--qb-orange-400)'}>
            {it.eye}
          </Eyebrow>
          <h3
            style={{
              fontFamily: 'var(--qb-font-display)',
              textTransform: 'uppercase',
              fontSize: 22,
              margin: 0,
              lineHeight: 1.1,
            }}>
            {it.t}
          </h3>
          <p
            style={{
              fontFamily: 'var(--qb-font-body)',
              fontSize: 14,
              lineHeight: 1.6,
              color: 'var(--qb-fg-muted)',
              margin: 0,
            }}>
            {it.b}
          </p>
        </button>
      );
    })}
  </div>
);

export const StageBuild = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = BUILD_ITEMS[activeIndex];
  return (
    <section
      id="build"
      className="qb-section-tight"
      style={{ padding: '120px 40px', borderBottom: '1px solid var(--qb-border-soft)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <SectionHeader
          num="01"
          eyebrow="BUILD · THE ENGINE"
          title="Capture your game as a Ruleset."
          side
          lead="Quest Bound's engine handles the heavy machinery — attributes, archetypes, derived stats, conditions, charts. Author once, automate forever."
        />
        <div
          className="qb-stage-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.15fr 1fr',
            gap: 40,
            alignItems: 'stretch',
          }}>
          <BuildEditorMock src={active.img} alt={active.alt} />
          <BuildSideCards activeIndex={activeIndex} onSelect={setActiveIndex} />
        </div>
      </div>
    </section>
  );
};

const StorefrontMock = () => (
  <div
    style={{
      background: 'var(--qb-ink-700)',
      border: '1px solid var(--qb-border-strong)',
      borderRadius: 6,
      overflow: 'hidden',
      boxShadow: 'var(--qb-shadow-lg)',
      display: 'flex',
    }}>
    <img
      src={storeImg}
      alt="Quest Bound storefront — Ruleset cover, pricing, modules, and a Buy button"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    />
  </div>
);

const PublishSideCards = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div
      style={{
        background: 'var(--qb-ink-700)',
        border: '1px solid var(--qb-border-strong)',
        borderRadius: 6,
        padding: '26px 26px',
      }}>
      <Eyebrow>SET UP IN MINUTES</Eyebrow>
      <h3
        className="qb-build-card-h3"
        style={{
          fontFamily: 'var(--qb-font-display)',
          textTransform: 'uppercase',
          fontSize: 26,
          lineHeight: 1.05,
          margin: '10px 0 14px',
        }}>
        Your storefront, on questbound.com
      </h3>
      <ul style={{ ...listReset, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          'Custom URL: questbound.com/your-press',
          'Stripe payouts — direct to your bank',
          'Bundle Rulesets, Modules, and updates',
          'Discount codes and gifting built in',
        ].map((s) => (
          <li
            key={s}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'baseline',
              fontFamily: 'var(--qb-font-body)',
              fontSize: 14,
              color: 'var(--qb-fg-muted)',
            }}>
            <span
              style={{
                color: 'var(--qb-orange-500)',
                fontFamily: 'var(--qb-font-display)',
                fontSize: 12,
              }}>
              +
            </span>
            {s}
          </li>
        ))}
      </ul>
    </div>

    <div
      style={{
        background: 'var(--qb-ink-900)',
        border: '1px solid var(--qb-orange-700)',
        borderRadius: 6,
        padding: '24px 26px',
        boxShadow: 'var(--qb-shadow-stamp)',
      }}>
      <Eyebrow>PUBLISHER PROGRAM</Eyebrow>
      <h3
        style={{
          fontFamily: 'var(--qb-font-display)',
          textTransform: 'uppercase',
          fontSize: 22,
          lineHeight: 1.1,
          margin: '10px 0 10px',
        }}>
        One subscription. The whole pipeline.
      </h3>
      <p
        style={{
          fontFamily: 'var(--qb-font-body)',
          fontSize: 14,
          lineHeight: 1.6,
          color: 'var(--qb-fg-muted)',
          margin: '0 0 16px',
        }}>
        One monthly price + 15% on each sale unlocks publishing, payouts, dashboard and customer
        accounts. No extra fees, no ceilings, no gotchas.
      </p>
      <Btn size="md" href="#pricing">See Publisher pricing →</Btn>
    </div>
  </div>
);

export const StagePublish = () => (
  <section
    id="publish"
    className="qb-section-tight"
    style={{
      padding: '120px 40px',
      background: 'var(--qb-ink-800)',
      borderBottom: '1px solid var(--qb-border-soft)',
      position: 'relative',
    }}>
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <SectionHeader
        num="02"
        eyebrow="PUBLISH · THE STOREFRONT"
        title="Spin up a sales page in an afternoon."
        side
        lead="Every Ruleset can ship as a storefront. Cover art, pricing, modules, screenshots, and a Buy button connected to Stripe — no separate website required."
      />
      <div
        className="qb-stage-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 40,
          alignItems: 'stretch',
        }}>
        <StorefrontMock />
        <PublishSideCards />
      </div>
    </div>
  </section>
);

const PlaySideContent = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div>
      <Eyebrow>PROGRESSIVE WEB APP</Eyebrow>
      <h3
        className="qb-play-h3"
        style={{
          fontFamily: 'var(--qb-font-display)',
          textTransform: 'uppercase',
          fontSize: 32,
          lineHeight: 1,
          margin: '10px 0 14px',
        }}>
        Installable. <span style={{ color: 'var(--qb-orange-500)' }}>Offline-ready.</span> On every
        device.
      </h3>
      <p
        style={{
          fontFamily: 'var(--qb-font-body)',
          fontSize: 16,
          lineHeight: 1.6,
          color: 'var(--qb-fg-muted)',
          margin: 0,
        }}>
        Players install the game like an app. Quest Bound runs the rules during the session — you
        and your players run the table.
      </p>
    </div>

    <div className="qb-play-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
      {[
        { eye: 'BUILT TO PLAY', t: 'Auto-rolls, conditions, derived stats' },
        { eye: 'MULTIPLAYER', t: 'Shared sessions, Campaign Dashboard, dice log' },
        { eye: 'DDDICE INTEGRATION', t: 'Real 3D dice rolls, synced to every player' },
        { eye: 'OFFLINE FIRST', t: 'Game night without WiFi, no problem' },
      ].map((c) => (
        <div
          key={c.t}
          style={{
            background: 'var(--qb-ink-800)',
            border: '1px solid var(--qb-border-strong)',
            borderRadius: 6,
            padding: '16px 18px',
          }}>
          <Eyebrow>{c.eye}</Eyebrow>
          <div
            style={{
              fontFamily: 'var(--qb-font-body)',
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--qb-fg)',
              marginTop: 6,
            }}>
            {c.t}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const StagePlay = () => (
  <section
    id="play"
    className="qb-section-tight"
    style={{ padding: '120px 40px', borderBottom: '1px solid var(--qb-border-soft)' }}>
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <SectionHeader
        num="03"
        eyebrow="PLAY · THE PLAYER APP"
        title="Players get a real app, not a PDF."
        side
        lead="Every Ruleset you publish is a working application. Players can run it in the browser or on their device, at the table or online. The engine runs the math, and you keep telling the story."
      />

      <div
        className="qb-stage-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 40,
          alignItems: 'center',
        }}>
        <div
          style={{
            background: 'var(--qb-ink-700)',
            border: '1px solid var(--qb-border-strong)',
            borderRadius: 6,
            overflow: 'hidden',
            boxShadow: 'var(--qb-shadow-lg)',
            display: 'flex',
          }}>
          <img
            src={spellsImg}
            alt="Quest Bound player app — spell sheet view"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </div>
        <PlaySideContent />
      </div>
    </div>
  </section>
);

const DashboardMock = () => (
  <div
    style={{
      background: 'var(--qb-ink-700)',
      border: '1px solid var(--qb-border-strong)',
      borderRadius: 6,
      overflow: 'hidden',
      boxShadow: 'var(--qb-shadow-lg)',
      display: 'flex',
    }}>
    <img
      src={metricsImg}
      alt="Quest Bound Publisher Dashboard — revenue, sales, active players, and recent activity"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    />
  </div>
);

export const StageMeasure = () => (
  <section
    id="measure"
    className="qb-section-tight"
    style={{
      padding: '120px 40px',
      background: 'var(--qb-ink-800)',
      borderBottom: '1px solid var(--qb-border-soft)',
    }}>
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <SectionHeader
        num="04"
        eyebrow="MEASURE · THE DASHBOARD"
        title="Watch your game live in the wild."
        side
        lead="Sales, sessions, characters created, modules installed. The Publisher Dashboard shows what's working — so the next Ruleset can be even better."
      />
      <DashboardMock />
    </div>
  </section>
);
